import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Drawer, Typography, Button, TextField, IconButton, Grid, MenuItem,
  CircularProgress, Alert, Divider, InputAdornment, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tooltip,
} from '@mui/material';
import {
  Close, AccountBalanceWallet, Save, Edit, Delete, InboxOutlined,
  RestartAlt, WarningAmberOutlined,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  bankDepositsURL, bankDepositsAddURL, bankDepositsUpdateURL, bankDepositsDeleteURL,
} from '../BaseURL';

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('church_token')}` });
const swalTop = { customClass: { container: 'swal-on-top' } };
const fmt = (v) => Number(v || 0).toLocaleString('en-GH', { minimumFractionDigits: 2 });
const shortDate = (d) => new Date(d).toLocaleDateString('en-GH', { day: '2-digit', month: 'short' });

const isBankToBank = (d) => (d?.narration || '').trim().toUpperCase() === 'BANK TO BANK';

const emptyForm = (dateDefault) => ({
  id: null,
  account_id: '', amount: '',
  deposit_date: dateDefault,
  narration: '',
});

export default function CashToBankDrawer({
  open, onClose, monthYear, periodFirstDay, periodLastDay,
  accounts = [], cashInHand = 0, onSaved,
  initialEntry = null,
}) {
  const [form, setForm]       = useState(emptyForm(periodFirstDay));
  const [deposits, setDeps]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const isEditing = form.id != null;

  const loadDeposits = useCallback(() => {
    setLoading(true);
    axios.get(`${bankDepositsURL}?month_year=${monthYear}`, { headers: headers() })
      .then(r => setDeps((r.data.data || []).filter(d => !isBankToBank(d))))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [monthYear]);

  useEffect(() => {
    if (!open) return;
    setError('');
    if (initialEntry) {
      setForm({
        id: initialEntry.id,
        account_id: initialEntry.account_id || '',
        amount: String(initialEntry.amount ?? ''),
        deposit_date: initialEntry.deposit_date?.slice(0, 10) || periodFirstDay,
        narration: initialEntry.narration || '',
      });
    } else {
      setForm(emptyForm(periodFirstDay));
    }
    loadDeposits();
  }, [open, periodFirstDay, loadDeposits, initialEntry]);

  const validate = () => {
    if (!form.account_id) return 'Select a bank account.';
    if (!form.deposit_date) return 'Deposit date is required.';
    if (periodFirstDay && (form.deposit_date < periodFirstDay || form.deposit_date > periodLastDay)) {
      return `Date must be within ${monthYear}.`;
    }
    if (!form.amount || Number(form.amount) <= 0) return 'Enter a valid amount.';
    return null;
  };

  const resetForm = () => {
    setForm(emptyForm(periodFirstDay));
    setError('');
  };

  const save = async () => {
    const err = validate();
    if (err) { setError(err); return; }

    const amt = Number(form.amount);
    // Soft-warn when the deposit would push cash-in-hand negative.
    // (For edits, add back the row's own contribution first so we don't double-count.)
    const currentRowAmt = isEditing
      ? Number(deposits.find(d => d.id === form.id)?.amount || 0)
      : 0;
    const effectiveCash = Number(cashInHand) + currentRowAmt;
    if (amt > effectiveCash) {
      const shortfall = amt - effectiveCash;
      const confirm = await Swal.fire({
        ...swalTop,
        icon: 'warning',
        iconColor: '#C89211',
        title: 'Not enough cash in hand',
        html: `
          <div style="text-align:left;font-size:14px;line-height:1.5;">
            You are about to deposit <strong>GH₵ ${fmt(amt)}</strong>,
            but only <strong>GH₵ ${fmt(effectiveCash)}</strong> is currently in hand.<br><br>
            Cash-in-hand will go <strong style="color:#B91C1C">−GH₵ ${fmt(shortfall)}</strong> after this entry.<br><br>
            Continue anyway?
          </div>`,
        showCancelButton: true,
        confirmButtonColor: '#C89211',
        confirmButtonText: 'Yes, proceed',
        cancelButtonText: 'Cancel',
      });
      if (!confirm.isConfirmed) return;
    }

    setSaving(true); setError('');
    const acc = accounts.find(a => a.id === Number(form.account_id));
    try {
      const payload = { ...form, account_name: acc?.name };
      if (isEditing) {
        await axios.post(bankDepositsUpdateURL, payload, { headers: headers() });
      } else {
        await axios.post(bankDepositsAddURL, payload, { headers: headers() });
      }
      Swal.fire({
        ...swalTop, icon: 'success',
        title: isEditing ? 'Deposit updated' : 'Deposit saved',
        text: `GH₵ ${fmt(form.amount)} → ${acc?.name}`,
        timer: 1200, showConfirmButton: false,
      });
      resetForm();
      loadDeposits();
      onSaved?.();
    } catch (e) {
      setError(e.response?.data?.message || 'Could not save deposit.');
    } finally {
      setSaving(false);
    }
  };

  const editDeposit = (d) => {
    setError('');
    setForm({
      id: d.id,
      account_id: d.account_id || '',
      amount: String(d.amount ?? ''),
      deposit_date: d.deposit_date?.slice(0, 10) || periodFirstDay,
      narration: d.narration || '',
    });
    document.querySelector('#deposit-drawer-body')?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteDeposit = (d) => {
    Swal.fire({
      ...swalTop, title: 'Delete this deposit?',
      text: `${d.account_name} — GH₵ ${fmt(d.amount)}`,
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#B91C1C', confirmButtonText: 'Delete',
    }).then(async res => {
      if (!res.isConfirmed) return;
      try {
        await axios.post(bankDepositsDeleteURL, { id: d.id }, { headers: headers() });
        if (form.id === d.id) resetForm();
        loadDeposits();
        onSaved?.();
      } catch (e) {
        Swal.fire({ ...swalTop, icon: 'error', title: 'Cannot delete', text: e.response?.data?.message || 'Delete failed.' });
      }
    });
  };

  const monthTotal = deposits.reduce((s, d) => s + Number(d.amount || 0), 0);
  const willOverdraw = Number(form.amount) > 0 && Number(form.amount) > (
    Number(cashInHand) + (isEditing ? Number(deposits.find(d => d.id === form.id)?.amount || 0) : 0)
  );

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, borderRadius: 0 } }}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 2,
          background: 'linear-gradient(90deg, #0B2545, #13315C)', color: '#fff', px: 3, py: 2.5,
          flexShrink: 0,
        }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 1.5, bgcolor: 'secondary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(200,146,17,0.35)',
          }}>
            <AccountBalanceWallet />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {isEditing ? 'Edit Deposit' : 'Cash → Bank Deposit'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              {monthYear} · {deposits.length} deposit{deposits.length === 1 ? '' : 's'} · GH₵ {fmt(monthTotal)}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}><Close /></IconButton>
        </Box>

        {/* Scrollable body */}
        <Box id="deposit-drawer-body" sx={{ flex: 1, overflowY: 'auto' }}>
          {/* Cash-in-hand card */}
          <Box sx={{ px: 3, pt: 3 }}>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 2,
              p: 2, borderRadius: 2,
              bgcolor: cashInHand < 0 ? 'rgba(185,28,28,0.06)' : 'rgba(11,37,69,0.04)',
              border: `1px solid ${cashInHand < 0 ? 'rgba(185,28,28,0.2)' : '#E2E8F0'}`,
            }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: 1.5,
                bgcolor: cashInHand < 0 ? 'error.main' : 'primary.main', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <AccountBalanceWallet />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}
                  sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 10 }}>
                  Cash In Hand
                </Typography>
                <Typography variant="h5" fontWeight={800}
                  color={cashInHand < 0 ? 'error.main' : 'primary.main'}
                  sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.15 }}>
                  GH₵ {fmt(cashInHand)}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Form */}
          <Box sx={{ px: 3, pt: 3, pb: 2 }}>
            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {willOverdraw && (
              <Alert
                severity="warning" icon={<WarningAmberOutlined />}
                sx={{ mb: 2, borderRadius: 2 }}
              >
                This deposit is larger than the cash currently in hand. You'll be asked to confirm before it saves.
              </Alert>
            )}

            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
              <Typography variant="overline" fontWeight={700} color="primary.main" sx={{ letterSpacing: '0.14em' }}>
                {isEditing ? 'Editing deposit' : 'New Deposit'}
              </Typography>
              {isEditing && (
                <Button size="small" startIcon={<RestartAlt />} onClick={resetForm}>
                  New deposit
                </Button>
              )}
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  select fullWidth size="small" label="Deposit To Account" required
                  value={form.account_id}
                  onChange={e => setForm({ ...form, account_id: e.target.value })}
                >
                  <MenuItem value=""><em>— Select account —</em></MenuItem>
                  {accounts.map(a => (
                    <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth size="small" label="Date" type="date" required
                  value={form.deposit_date}
                  inputProps={{ min: periodFirstDay, max: periodLastDay }}
                  onChange={e => setForm({ ...form, deposit_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth size="small" label="Amount" type="number" required
                  value={form.amount}
                  onChange={e => setForm({ ...form, amount: e.target.value })}
                  inputProps={{ min: 0, step: '0.01' }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">GH₵</InputAdornment>,
                    sx: { fontVariantNumeric: 'tabular-nums', fontWeight: 700 },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth size="small" label="Narration"
                  placeholder="Cash to Bank"
                  value={form.narration}
                  onChange={e => setForm({ ...form, narration: e.target.value })}
                  multiline minRows={2}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Sub-header */}
          <Box sx={{
            px: 2, py: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            bgcolor: '#F8FAFC',
            borderTop: '1px solid #E2E8F0',
            borderBottom: '1px solid #E2E8F0',
          }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: 10 }}>
              This Month's Deposits
            </Typography>
            <Typography variant="caption" fontWeight={700} color="text.disabled"
              sx={{ fontSize: 10, fontVariantNumeric: 'tabular-nums' }}>
              {deposits.length}
            </Typography>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress size={22} /></Box>
          ) : deposits.length === 0 ? (
            <Box sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              px: 3, py: 4, color: 'text.secondary', textAlign: 'center',
            }}>
              <InboxOutlined sx={{ fontSize: 36, color: '#CBD5E1', mb: 1 }} />
              <Typography variant="body2" fontWeight={600}>No deposits yet for {monthYear}</Typography>
              <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5 }}>
                Fill the form above and click Save.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={70}>Date</TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell align="right">Amount (GH₵)</TableCell>
                    <TableCell align="right" width={80}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deposits.map(d => {
                    const active = form.id === d.id;
                    return (
                      <TableRow key={d.id} hover selected={active}
                        sx={{
                          bgcolor: active ? 'rgba(200,146,17,0.08) !important' : undefined,
                          '& td:first-of-type': {
                            borderLeft: active ? '3px solid' : '3px solid transparent',
                            borderColor: active ? 'secondary.main' : 'transparent',
                          },
                        }}>
                        <TableCell sx={{ color: 'text.secondary', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                          {shortDate(d.deposit_date)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} noWrap>{d.account_name}</Typography>
                          {d.narration && d.narration.trim().toLowerCase() !== 'cash to bank' && (
                            <Typography variant="caption" color="text.secondary" noWrap display="block">
                              {d.narration}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{
                          fontWeight: 700, color: 'primary.main',
                          fontVariantNumeric: 'tabular-nums',
                        }}>
                          {fmt(d.amount)}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => editDeposit(d)} sx={{ p: 0.3 }}>
                              <Edit sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => deleteDeposit(d)} sx={{ p: 0.3 }}>
                              <Delete sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow sx={{ bgcolor: '#F1F5F9' }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 800 }}>Total</TableCell>
                    <TableCell align="right" sx={{
                      fontWeight: 800, color: 'primary.main',
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {fmt(monthTotal)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Footer */}
        <Divider />
        <Stack direction="row" spacing={1.5} sx={{ px: 3, py: 2, bgcolor: '#FAFBFC', flexShrink: 0 }} justifyContent="flex-end">
          <Button onClick={onClose}>Close</Button>
          <Button
            variant="contained" onClick={save} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            sx={{ px: 3 }}
          >
            {isEditing ? 'Save Changes' : 'Save Deposit'}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}
